<template>
  <jz-container flex column overflow-y-auto>
    <layout-full-card
      :back-fallback="backRoute"
      id="course-details-view"
      :loading="!courseDetails"
      :breadcrumbs="breadcrumbs"
    >
      <jz-container v-if="courseDetails" id="course-details" flex column overflow-y-auto>
        <img class="bookmark-icon" :src="bookmarkIcon" @click="toggleBookmark" />

        <jz-container class="course-image-container">
          <jz-container class="course-image">
            <img :src="courseDetails.image" />
          </jz-container>
        </jz-container>

        <jz-title>
          <jz-icon :src="booksIcon" />

          {{ courseDetails.title }}
        </jz-title>

        <jz-container class="detail-section">
          <course-tags :levels="courseDetails.levels" :categories="courseDetails.categories" />

          {{ /** TODO: HTML needs to be disallowed if users are creating content! */ }}
          <p
            v-for="(paragraph, index) in courseDetails.description"
            :key="index"
            v-html="paragraph"
          />
        </jz-container>

        <jz-container class="series-buttons">
          <router-link :to="previousCoursePath" v-if="previousCourse">
            <outline-button>
              {{ previousCourseButtonText }}
            </outline-button>
          </router-link>

          <router-link class="next" :to="nextCoursePath" v-if="nextCourse">
            <outline-button>
              {{ nextCourseButtonText }}
            </outline-button>
          </router-link>
        </jz-container>

        <jz-title>
          <jz-icon :src="defaultTrophyIcon" />

          {{ "course.details.trophies" | __ }}
        </jz-title>

        <jz-container class="detail-section">
          <div class="trophy-encouragement">{{ "course.details.encouragement" | __ }}</div>

          <jz-container v-if="trophies" id="course-details-trophies" flex row>
            <div id="earned-trophy-total">
              <span class="trophy-total">{{ trophies.totalEarned }}</span>
              <span class="trophy-possible"> / {{ trophies.max }}</span>
            </div>
            <span class="possible-trophies">
              <img :src="goldTrophyIcon" class="gold" />
              {{ trophies.gold }}
            </span>
            <span class="possible-trophies">
              <img :src="silverTrophyIcon" class="silver" />
              {{ trophies.silver }}
            </span>
            <span class="possible-trophies">
              <img :src="bronzeTrophyIcon" class="bronze" />
              {{ trophies.bronze }}
            </span>
          </jz-container>
        </jz-container>
      </jz-container>

      <jz-container v-if="courseDetails" id="course-chapters" flex column overflow-y-auto>
        <exercise-list
          v-for="(item, index) in courseDetails.contents"
          :key="`${item.title}-${index}`"
          :item="item"
          :course-uuid="courseDetails.uuid"
          :course-history="courseHistory"
        />
      </jz-container>
    </layout-full-card>

    <feedback-popup v-if="showFeedbackForm" :logId="feedbackLogId" @close="handleFeedbackClose" />
  </jz-container>
</template>

<script>
import { mapState } from "vuex";
import {
  BREAKPOINTS,
  ROUTES,
  VUEX_CONTENT_ACTION_NAMES,
  VUEX_CONTENT_NAMESPACE,
  VUEX_USER_ACTION_NAMES,
  VUEX_USER_NAMESPACE
} from "@tt-app/constants";

import CourseTags from "@tt-app/components/course/CourseTags.vue";
import ExerciseList from "@tt-app/components/exercise/ExerciseList.vue";
import FeedbackPopup from "../../components/feedback/FeedbackPopup";
import LayoutFullCard from "@tt-app/layouts/LayoutFullCard.vue";
import OutlineButton from "@tt-app/components/ui/button/OutlineButton.vue";

import activeBookmarkIcon from "@joyz/app-core/assets/icons/active/Bookmark.svg";
import backIcon from "@joyz/app-core/assets/icons/black/Back.svg";
import booksIcon from "@joyz/app-core/assets/icons/default/Books.svg";
import bronzeTrophyIcon from "@joyz/app-core/assets/trophy/small/Bronze.svg";
import goldTrophyIcon from "@joyz/app-core/assets/trophy/small/Gold.svg";
import inactiveBookmarkIcon from "@joyz/app-core/assets/icons/black/Bookmark.svg";
import microphoneIcon from "@joyz/app-core/assets/icons/default/Microphone.svg";
import silverTrophyIcon from "@joyz/app-core/assets/trophy/small/Silver.svg";
import trophyIcon from "@joyz/app-core/assets/icons/default/Trophy.svg";

const {
  BOOKMARKED_COURSES_REQUEST,
  COURSE_DETAILS_REQUEST,
  TOGGLE_BOOKMARK
} = VUEX_CONTENT_ACTION_NAMES;
const { COURSE_HISTORY_REQUEST } = VUEX_USER_ACTION_NAMES;

export default {
  name: "CourseDetail",

  components: { CourseTags, ExerciseList, FeedbackPopup, LayoutFullCard, OutlineButton },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.feedbackParams = to.params.feedbackParams;

      if (!from.path.match(/exercise/)) {
        vm.$store.commit("courseDetailsEntryRoute", from);
      }
    });
  },

  beforeRouteLeave(to, from, next) {
    if (!to.path.match(/exercise/)) {
      this.$store.commit("courseDetailsEntryRoute", undefined);
    }
    next();
  },

  mounted() {
    window.addEventListener("resize", this.updateScreenWidth);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updateScreenWidth);
  },

  data() {
    return {
      from: undefined,
      feedbackParams: undefined,
      screenWidth: window.innerWidth
    };
  },

  computed: {
    ...mapState({
      bookmarkedCourses: state => state.content.bookmark.courses,
      courseDetails: state => state.content.course.details,
      courseDetailsEntryRoute: state => state.navigation.courseDetails.entryRoute,
      courseHistory: state => state.user.course.history
    }),

    backRoute() {
      if (this.courseDetailsEntryRoute) {
        return this.courseDetailsEntryRoute;
      }

      return {
        name: ROUTES.HOME.NAME
      };
    },

    bookmarkIcon() {
      return this.currentlyBookmarked ? this.activeBookmarkIcon : this.inactiveBookmarkIcon;
    },

    breadcrumbs() {
      const title = (this.courseDetails && this.courseDetails.title) || "";
      if (
        this.courseDetailsEntryRoute &&
        this.courseDetailsEntryRoute.path.match(/course\/explore/)
      ) {
        return [this.$__("nav.home"), this.$__("nav.search"), title];
      } else {
        return [this.$__("nav.home"), title];
      }
    },

    currentlyBookmarked() {
      return this.bookmarkedCourses.some(course => {
        return course.uuid === this.courseUuid;
      });
    },

    feedbackLogId() {
      if (!this.feedbackParams) return;

      return this.feedbackParams.logId;
    },

    nextCourse() {
      return this.courseDetails.seriesNavigation.next;
    },

    nextCoursePath() {
      const nextCourseUuid = this.nextCourse;
      return {
        name: ROUTES.COURSE.DETAILS.NAME,
        params: {
          courseUuid: nextCourseUuid
        }
      };
    },

    nextCourseButtonText() {
      const text = this.$__("course.series.next");
      const mobileText = this.$__("course.series.next.mobile");

      return this.onMobile ? mobileText : text;
    },

    onMobile() {
      return this.screenWidth < BREAKPOINTS.MOBILE;
    },

    previousCourse() {
      return this.courseDetails.seriesNavigation.prev;
    },

    previousCoursePath() {
      const previousCourseUuid = this.previousCourse;
      return {
        name: ROUTES.COURSE.DETAILS.NAME,
        params: {
          courseUuid: previousCourseUuid
        }
      };
    },

    previousCourseButtonText() {
      const text = this.$__("course.series.previous");
      const mobileText = this.$__("course.series.previous.mobile");

      return this.onMobile ? mobileText : text;
    },

    previousPath() {
      return this.courseDetailsEntryRoute && this.courseDetailsEntryRoute.path;
    },

    showFeedbackForm() {
      if (!this.feedbackParams) return false;

      return this.feedbackParams.withFeedback === "true";
    },

    trophies() {
      let trophies;

      if (this.courseHistory && this.courseHistory.progress) {
        const max = this.courseHistory.progress.numMaxTrophies;
        const gold = this.courseHistory.progress.numEarnedTrophies[3];
        const silver = this.courseHistory.progress.numEarnedTrophies[2];
        const bronze = this.courseHistory.progress.numEarnedTrophies[1];
        const totalEarned = gold + silver + bronze;

        trophies = {
          bronze,
          totalEarned,
          gold,
          max,
          silver
        };
      }

      return trophies;
    }
  },

  created() {
    const {
      $router: {
        currentRoute: {
          params: { courseUuid }
        }
      }
    } = this;
    this.courseUuid = courseUuid;

    this.activeBookmarkIcon = activeBookmarkIcon;
    this.backIcon = backIcon;
    this.booksIcon = booksIcon;
    this.bronzeTrophyIcon = bronzeTrophyIcon;
    this.goldTrophyIcon = goldTrophyIcon;
    this.inactiveBookmarkIcon = inactiveBookmarkIcon;
    this.silverTrophyIcon = silverTrophyIcon;
    this.defaultTrophyIcon = trophyIcon;

    this.courseTypeIcons = {
      1: microphoneIcon
    };
    this.courseTypes = {
      1: "Conversation"
    };
    this.fetchCourseDetails();
  },

  methods: {
    async fetchCourseDetails() {
      const historyRequest = this.$store.dispatch(
        `${VUEX_USER_NAMESPACE}/${COURSE_HISTORY_REQUEST}`,
        { courseUuid: this.courseUuid }
      );
      this.$store.dispatch(`${VUEX_CONTENT_NAMESPACE}/${BOOKMARKED_COURSES_REQUEST}`);
      const detailsRequest = this.$store.dispatch(
        `${VUEX_CONTENT_NAMESPACE}/${COURSE_DETAILS_REQUEST}`,
        { courseUuid: this.courseUuid }
      );
      await Promise.all([detailsRequest, historyRequest]);
    },

    handleFeedbackClose() {
      this.feedbackParams.withFeedback = false;
    },

    toggleBookmark() {
      this.$store.dispatch(`${VUEX_CONTENT_NAMESPACE}/${TOGGLE_BOOKMARK}`, {
        courseUuid: this.courseUuid
      });
    },

    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    }
  }
};
</script>

<style lang="scss">
#top-navigation {
  align-items: center;
  display: flex;
  flex-direction: row;

  .tt-bread-crumb-nav {
    margin: 0 0 0 $gutter;
  }
}

#course-details-view.layout-full-card {
  padding: $gutter ($gutter / 2) ($gutter / 2) ($gutter / 2);
  height: 100%;

  .jz-card.content {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    margin: 0 0 ($gutter / 2) 0;
    max-width: 664px;
    position: relative;

    .bookmark-icon {
      background: linear-gradient(
        -180deg,
        rgba(247, 247, 247, 0.8) 0%,
        rgba(247, 247, 247, 0.4) 100%
      );
      border-radius: 0 0 ($gutter / 2) ($gutter / 2);
      height: 64px;
      left: 0;
      margin: 0 0 0 $gutter;
      position: absolute;
      top: 0;
      z-index: 1;

      &:hover {
        cursor: pointer;
      }
    }

    #course-details {
      border-radius: 12px;

      .course-image-container {
        height: 0;
        overflow: hidden;
        padding-top: 444px / 666px * 100%;
        background: white;
        position: relative;

        .course-image {
          height: 100%;
          left: 0;
          position: absolute;
          text-align: center;
          top: 0;
          width: 100%;

          img {
            height: auto;
            max-height: 100%;
            max-width: 100%;
            object-fit: contain;
            width: auto;
          }
        }
      }

      .detail-section {
        display: grid;
        gap: ($gutter / 2);
        padding: $gutter;

        p {
          color: $color-outer-space;
          font-size: 14px;
          margin: 0;
          word-break: break-all;
        }

        p + p {
          padding-top: 0;
        }
      }

      .series-buttons {
        display: flex;
        flex-direction: row;
        padding: $gutter;

        a:hover {
          color: unset;
          text-decoration: none;
        }

        .next {
          margin-left: auto;
        }
      }

      .trophy-encouragement {
        color: var(--color-secondary);
        font-size: $font-size-xs;
        font-weight: bold;
      }

      #course-details-trophies {
        align-items: baseline;
        display: grid;
        gap: ($gutter / 2);
        grid-template-areas: "earned-trophies  earned-trophies earned-trophies" "gold silver bronze";
        grid-template-columns: auto auto auto;
        grid-template-rows: auto auto;

        img {
          height: 48px;
        }

        #earned-trophy-total {
          align-self: center;
          grid-area: earned-trophies;

          .earned-trophy-title {
            color: $color-outer-space;
            font-size: 12px;
          }

          .trophy-possible {
            color: $color-cadet-grey;
            font-size: 16px;
          }
        }

        .possible-trophies {
          color: $color-outer-space;
          font-size: 20px;
          font-weight: bold;
          height: 40px;

          .gold {
            grid-area: gold;
          }

          .silver {
            grid-area: silver;
          }

          .bronze {
            grid-area: bronze;
          }
        }
      }
    }

    #course-chapters {
      border-top: 1px solid rgba(var(--rgb-black), 0.08);
    }
  }
}

@include responsive(tablet) {
  #course-details-view.layout-full-card {
    padding: $gutter $gutter 0 $gutter;

    .jz-card.content {
      margin: 0 0 $gutter 0;

      #course-details {
        #course-details-trophies {
          grid-template-areas: "earned-trophies gold silver bronze";
          grid-template-columns: 84px 92px 92px 92px;
          grid-template-rows: auto;
        }
      }
    }
  }
}

@include responsive(desktop) {
  #course-details-view.layout-full-card {
    .jz-card.content {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto;
      margin: 0;
      max-width: 920px;
      overflow: hidden;

      #course-details {
        border-radius: 0;
        border-right: 1px solid rgba(var(--rgb-black), 0.08);
      }

      #course-chapters {
        border-top: none;
        overflow: auto;
      }
    }
  }
}
</style>
